import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Stack
} from '@mui/material';
import { Task } from '../types';
import VoiceRecorder from './VoiceRecorder';
import { analyzeTask, TaskSuggestion } from '../services/geminiService';

interface MentalDeclutterProps {
  onTasksExtracted: (tasks: Task[]) => void;
}

const MentalDeclutter: React.FC<MentalDeclutterProps> = ({ onTasksExtracted }) => {
  const [open, setOpen] = useState(false);
  const [thoughts, setThoughts] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleRecordingComplete = async (audioBlob: Blob, transcription: string, suggestions: TaskSuggestion[]) => {
    if (transcription.trim()) {
      setThoughts(prev => {
        const newThoughts = prev ? `${prev}\n\n${transcription}` : transcription;
        return newThoughts;
      });

      // Create tasks from voice recording suggestions
      const tasks = suggestions.map(suggestion => ({
        id: crypto.randomUUID(),
        title: suggestion.title,
        description: suggestion.description,
        quadrant: suggestion.quadrant,
        completed: false,
        createdAt: new Date(),
      }));
      
      onTasksExtracted(tasks);
      setSuggestions(prev => [
        ...prev,
        ...suggestions.map(s => `${s.title} (${s.description}) → Quadrant ${s.quadrant}`)
      ]);
    }
  };

  const processThoughts = async () => {
    setLoading(true);
    setSuggestions([]);
    try {
      // Split thoughts into individual tasks (by comma or newline)
      const items = thoughts
        .split(/\n|,/)
        .map(item => item.trim())
        .filter(Boolean);

      const aiResults = await Promise.all(
        items.map(async (item) => {
          try {
            const suggestion = await analyzeTask(item);
            return suggestion;
          } catch (err) {
            return null;
          }
        })
      );

      const validResults: TaskSuggestion[] = aiResults.filter((result): result is TaskSuggestion => result !== null);
      const tasks = validResults.map((result) => ({
        id: crypto.randomUUID(),
        title: result.title,
        description: result.description,
        quadrant: result.quadrant,
        completed: false,
        createdAt: new Date(),
      }));
      onTasksExtracted(tasks);
      setSuggestions(
        validResults.map(
          (result) => `${result.title} (${result.description}) → Quadrant ${result.quadrant}`
        )
      );
    } catch (error) {
      console.error('Error processing thoughts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button 
        variant="contained" 
        color="primary" 
        onClick={handleOpen}
        sx={{ mb: 2 }}
      >
        Declutter My Mind
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Mental Decluttering</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Share what's on your mind, and I'll help you organize your thoughts using the Eisenhower Matrix.
          </Typography>
          
          <Stack spacing={2}>
            <VoiceRecorder onRecordingComplete={handleRecordingComplete} />
            
            <TextField
              multiline
              rows={6}
              fullWidth
              value={thoughts}
              onChange={(e) => setThoughts(e.target.value)}
              placeholder="Type or paste your thoughts here..."
              variant="outlined"
            />
          </Stack>

          {loading && (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          )}

          {suggestions.length > 0 && (
            <Box mt={2}>
              <Typography variant="h6" gutterBottom>
                Suggestions:
              </Typography>
              {suggestions.map((suggestion, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                  • {suggestion}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={processThoughts} 
            variant="contained" 
            color="primary"
            disabled={!thoughts.trim() || loading}
          >
            Process Thoughts
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MentalDeclutter; 